import { useEffect, useCallback } from 'react';
import { usePlatformSettingsStore } from '@/store/platform-settings/store';
import { useThemeStore } from '@/store/theme/store';
import { PlatformSettingsService, parsePlatformSettings } from '@/core/services/platformSettingsService';
import { logger } from '@/core/utils/logger';

// Mapping from API key format to CSS variable name
const toCssVar = (key: string): string => {
  return `--${key.toLowerCase().replace(/_/g, '-')}`;
};

// Apply CSS custom properties for a theme
const applyThemeVariables = (themeSettings: Record<string, string>) => {
  const root = document.documentElement;

  Object.entries(themeSettings).forEach(([key, value]) => {
    if (value && value.trim() !== '') {
      const cssVar = toCssVar(key);
      root.style.setProperty(cssVar, value);
    }
  });
};

// Apply static colors as CSS variables
const applyStaticColors = (colors: Record<string, string>) => {
  const root = document.documentElement;

  const staticColorMapping: Record<string, string> = {
    'PRIMARY': '--primary',
    'SUCCESS': '--success',
    'DANGER': '--danger',
    'WARNING': '--warning',
    'INFO': '--info',
    'WHITE': '--white-color',
    'GRAY': '--gray-color',
    'CAPTION': '--caption',
    'BUTTON_INACTIVE': '--button-inactive-color',
  };

  Object.entries(colors).forEach(([key, value]) => {
    if (value && value.trim() !== '') {
      const cssVar = staticColorMapping[key] || toCssVar(key);
      root.style.setProperty(cssVar, value);
    }
  });
};

// Apply typography as CSS variables
const applyTypography = (typography: Record<string, string>) => {
  const root = document.documentElement;

  const typographyMapping: Record<string, string> = {
    'FONT_PRIMARY': '--font-primary',
    'FONT_SECONDARY': '--font-secondary',
    'FONT_LOGO': '--font-logo',
  };

  Object.entries(typography).forEach(([key, value]) => {
    if (value && value.trim() !== '') {
      const cssVar = typographyMapping[key] || toCssVar(key);
      root.style.setProperty(cssVar, value);
    }
  });
};

// Apply dimensions as CSS variables
const applyDimensions = (dimensions: Record<string, string>) => {
  const root = document.documentElement;

  const dimensionsMapping: Record<string, string> = {
    'SIDEBAR_WIDTH': '--sidebar-width',
    'TOPBAR_HEIGHT': '--topbar-height',
    'BREADCRUMB_HEIGHT': '--breadcrumb-height',
    'SCREEN_TABLET': '--screen-tablet',
    'SCREEN_MOBILE': '--screen-mobile',
  };

  Object.entries(dimensions).forEach(([key, value]) => {
    if (value && value.trim() !== '') {
      const cssVar = dimensionsMapping[key] || toCssVar(key);
      root.style.setProperty(cssVar, value);
    }
  });
};

// Update favicon dynamically
const updateFavicon = (url: string) => {
  if (!url || url.trim() === '') return;

  let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
};

// Update document title dynamically
const updateTitle = (title: string) => {
  if (!title || title.trim() === '') return;
  document.title = title;
};

interface PlatformSettingsProviderProps {
  children: React.ReactNode;
}

export default function PlatformSettingsProvider({ children }: PlatformSettingsProviderProps) {
  const settings = usePlatformSettingsStore((state) => state.settings);
  const setSettings = usePlatformSettingsStore((state) => state.setSettings);
  const setLoaded = usePlatformSettingsStore((state) => state.setLoaded);

  const theme = useThemeStore((state) => state.theme);
  const setTheme = useThemeStore((state) => state.setTheme);

  // Apply all settings for current theme
  const applyAllSettings = useCallback(() => {
    // Defensive check for themes object
    const themes = settings?.themes || { dark: {}, light: {} };
    const currentThemeSettings = themes[theme] || {};

    // Apply theme-specific variables
    applyThemeVariables(currentThemeSettings);

    // Apply static colors
    applyStaticColors(settings?.staticColors || {});

    // Apply typography
    applyTypography(settings?.typography || {});

    // Apply dimensions
    applyDimensions(settings?.dimensions || {});

    // Apply branding
    if (settings?.branding?.FAVICON_URL) {
      updateFavicon(settings.branding.FAVICON_URL);
    }
    if (settings?.branding?.PLATFORM_NAME) {
      updateTitle(settings.branding.PLATFORM_NAME);
    }

    // Handle forced theme
    const forceTheme = settings?.features?.FORCE_THEME;
    if (forceTheme && typeof forceTheme === 'string' && forceTheme.trim() !== '') {
      const forcedTheme = forceTheme.toLowerCase() as 'dark' | 'light';
      if ((forcedTheme === 'dark' || forcedTheme === 'light') && theme !== forcedTheme) {
        setTheme(forcedTheme);
      }
    }
  }, [settings, theme, setTheme]);

  // Apply settings on mount and when theme changes
  useEffect(() => {
    applyAllSettings();
  }, [applyAllSettings]);

  // Fetch settings from API on startup
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const rawSettings = await PlatformSettingsService.getSettings();
        const parsed = parsePlatformSettings(rawSettings);
        setSettings(parsed);
        setLoaded(true);
        logger.log('Platform settings loaded from API');
      } catch (error) {
        logger.error('Failed to fetch platform settings:', error);
        setLoaded(true); // Mark as loaded even on error to not block the app
      }
    };

    fetchSettings();
  }, [setSettings, setLoaded]);

  return <>{children}</>;
}
